import { logout } from '@/services/apis/login';
import {
  DashboardOutlined,
  LockOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useLocation, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Avatar, Space, Spin } from 'antd';
import { createStyles } from 'antd-style';
import { stringify } from 'querystring';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';
import ModalUpdatePassword from '../ModalUpdatePassword';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu }) => {
  // 获取当前路由
  const { pathname } = useLocation();
  const isAdmin = pathname.includes('/admin');
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await logout();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: stringify({
          redirect: pathname + search,
        }),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'updatePassword') {
      setShowUpdatePassword(true);
      return;
    }
    history.push(`${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const dashboardMenu = {
    key: '/',
    icon: <DashboardOutlined />,
    label: '网站前台',
  };

  const adminMenu = {
    key: '/admin',
    icon: <SafetyCertificateOutlined />,
    label: '管理后台',
  };

  const menuItems = [
    ...(menu
      ? [
          {
            key: '/profile',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'updatePassword',
            icon: <LockOutlined />,
            label: '修改密码',
          },
          ...(isAdmin ? [dashboardMenu] : [adminMenu]),
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <ModalUpdatePassword
        open={showUpdatePassword}
        onOpenChange={setShowUpdatePassword}
      />
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        <Space>
          <Avatar src={currentUser?.avatar} size={32} />
          <AvatarName />
        </Space>
      </HeaderDropdown>
    </>
  );
};
